/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

import SpineWebTypes from 'spine-web/proto/index';
import {ActorProvider, init, Client, HttpClient} from 'spine-web';
import LicenseServerModel from '@/../generated/main/js/index';
import {firebaseDatabase} from '@/api/firebase';
import {backendBaseUrl} from '@/api/backend/settings/connection-settings';
import AuthorizingHttpClient from '@/api/backend/authorizing-http-client';
import {auth} from '@/api/auth';
import actor from '@/api/backend/actor';

/**
 * Creates a new backend client with custom HTTP client.
 *
 * @param {ActorProvider} actor   a provider of the actor that associates requests
 *                                to the backend with an application user
 * @param {HttpClient} httpClient custom HTTP client
 */
function newBackend(actor: ActorProvider, httpClient: HttpClient): Client {
  /**
   * Options of Spine backend client that interacts with a backend directly
   * retrieving results from HTTP response.
   */
  const directBackendOptions = {
    endpointUrl: backendBaseUrl,
    actorProvider: actor,
  };

  /**
   * Options of Spine backend client that uses Firebase Realtime Database
   * for query/subscription results retrieval.
   */
  const firebaseBackendOptions = {
    endpointUrl: backendBaseUrl,
    firebaseDatabase: firebaseDatabase,
    actorProvider: actor,
  };

  const client = init({
    protoIndexFiles: [
      SpineWebTypes,
      LicenseServerModel,
    ],
    forCommands: directBackendOptions,
    forQueries: directBackendOptions,
    forSubscriptions: firebaseBackendOptions,
  });

  client._commanding._endpoint._httpClient = httpClient;
  client._querying._endpoint._httpClient = httpClient;
  client._subscribing._endpoint._httpClient = httpClient;

  return client;
}

/**
 * Creates an HTTP client that authorizes requests with a Firebase security token.
 */
function authorizingHttpClient(): AuthorizingHttpClient {
  return new AuthorizingHttpClient(backendBaseUrl, () => auth().getFirebaseToken());
}

/**
 * A client for the License Server backend for the current web app instance.
 */
let _backend: Client = null;

/**
 * Returns the backend client.
 *
 * <p>If the client is not initialized, creates a new instance.
 *
 * @return {Client}
 */
export default function backend(): Client {
  if (!_backend) {
    _backend = newBackend(actor, authorizingHttpClient());
  }
  return _backend;
}
